import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';
import PaginationControls from '../../components/PaginationControls';
import AgGridTable from '../../components/AgGridTable';

const MfaUserForm = ({ onClose, onSave, initialData = null, showOnlyOtp = false, autoGenerateOtp = false }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    application: '',
    user_type: 'RPA', // Default value
    origin_ip_whitelist: '', // Comma-separated IPs
    owner_name: '',
    owner_email: '',
    secret: '',
    is_enabled: true, // Add is_enabled to form data, default to true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formErrors, setFormErrors] = useState({}); // New state for field-specific errors
  const [successMessage, setSuccessMessage] = useState(null);
  const [otpCode, setOtpCode] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isOtpDisplayActive, setIsOtpDisplayActive] = useState(false); // New state to control OTP display
  const [copySuccess, setCopySuccess] = useState(''); // State for copy success message
  const [isSecretEditable, setIsSecretEditable] = useState(false); // New state for secret editability
  const [hasMfaSecretError, setHasMfaSecretError] = useState(false); // State to track MFA secret key errors
  const [mfaUserLogs, setMfaUserLogs] = useState([]); // State for MFA user logs
  const [mfaUserLogsLoading, setMfaUserLogsLoading] = useState(false); // State for loading logs
  const [mfaUserLogsError, setMfaUserLogsError] = useState(null); // State for log errors
  const [showMfaLogs, setShowMfaLogs] = useState(false); // State to toggle MFA logs visibility
  const [mfaLogsPagination, setMfaLogsPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_count: 0,
    page_size: 50,
    has_next: false,
    has_previous: false
  });
  const [mfaLogsCurrentPage, setMfaLogsCurrentPage] = useState(1);

  useEffect(() => {
    if (initialData) {
      setFormData({
        uuid: initialData.id || '',
        first_name: initialData.first_name || '',
        last_name: initialData.last_name || '',
        email: initialData.email || '',
        application: initialData.application || '',
        user_type: initialData.user_type || 'RPA',
        origin_ip_whitelist: Array.isArray(initialData.origin_ip_whitelist) ? initialData.origin_ip_whitelist.join(', ') : '',
        owner_name: initialData.owner_name || '',
        owner_email: initialData.owner_email || '',
        secret: '',
      });
      setIsSecretEditable(false); // Secret is not editable by default when loading existing user
    } else {
      setIsSecretEditable(true); // Secret is editable by default when creating a new user
    }
  }, [initialData]);

  // Update form data when initialData changes, including is_enabled
  useEffect(() => {
    if (initialData) {
      setFormData(prevData => ({
        ...prevData,
        is_enabled: initialData.is_enabled !== undefined ? initialData.is_enabled : true, // Set is_enabled from initialData or default to true
      }));
    }
  }, [initialData]);

  // Effect to automatically generate OTP if autoGenerateOtp prop is true
  useEffect(() => {
    if (autoGenerateOtp && initialData && !isOtpDisplayActive) {
      fetchOtpCode();
    }
  }, [autoGenerateOtp, initialData, isOtpDisplayActive]);

  // Effect to fetch logs when initialData changes and showOnlyOtp is true
  useEffect(() => {
    if (initialData && showOnlyOtp && initialData.email) {
      setMfaLogsCurrentPage(1); // Reset pagination
      fetchMfaUserLogs(initialData.email, 1);
    }
  }, [initialData, showOnlyOtp]);

  // Effect for auto-refreshing OTP
  useEffect(() => {
    let timer;
    if (isOtpDisplayActive && initialData && timeRemaining > 0) {
      timer = setInterval(() => {
        setTimeRemaining((prevTime) => prevTime - 1);
      }, 1000);
    } else if (isOtpDisplayActive && initialData && timeRemaining <= 0) {
      // If time runs out, fetch new OTP
      fetchOtpCode();
    }
    return () => clearInterval(timer);
  }, [isOtpDisplayActive, initialData, timeRemaining]);

  const fetchOtpCode = async () => {
    if (!initialData) return; // Only for existing users
    setLoading(true);
    setError(null);
    setHasMfaSecretError(false); // Reset MFA secret error flag
    try {
      const response = await adminApi.get(`/admin/mfa/users/${initialData.id}/code/get/`); // Corrected API path
      if (response.data && response.data.success) {
        setOtpCode(response.data.otp_code);
        setTimeRemaining(response.data.time_remaining);
        setIsOtpDisplayActive(true); // Activate display and auto-refresh
        fetchMfaUserLogs(initialData.email, mfaLogsCurrentPage); // Refresh logs after successful OTP generation
      } else {
        setError(response.data?.message || 'Failed to generate OTP.');
        setIsOtpDisplayActive(false); // Deactivate on failure
      }
    } catch (err) {
      console.error('Error generating OTP:', err);
      if (err.response?.data?.error === "2FA is not enabled for this MFA User.") {
        setError("2FA is currently disabled for this user. Please enable it to generate OTP codes.");
      } else if (err.response?.data?.error && err.response.data.error.includes("Invalid MFA secret key")) {
        // Handle corrupted secret key error with detailed solution
        const errorData = err.response.data;
        setHasMfaSecretError(true); // Set flag to disable TOTP generation
        setError(
          <div>
            <strong>Invalid MFA Secret Key</strong>
            <br />
            {errorData.details && (
              <>
                <span className="text-muted">{errorData.details}</span>
              </>
            )}
            <br />
            <div className="mt-2">
              <strong>Solution:</strong>
              <ol>
                <li>Get the current secret key from the third-party application</li>
                <li>Click Edit Secret to update the secret, then save the user</li>
                <li>Try generating TOTP again</li>
              </ol>
            </div>
          </div>
        );
      } else {
        setError(err.response?.data?.error || err.message || 'An error occurred while generating OTP.');
      }
      setIsOtpDisplayActive(false); // Deactivate on error
    } finally {
      setLoading(false);
    }
  };

  const fetchMfaUserLogs = async (email, page = 1) => {
    setMfaUserLogsLoading(true);
    setMfaUserLogsError(null);
    try {
      // Always use the specific MFA user logs endpoint since we have the ID
      if (!initialData || !initialData.id) {
        setMfaUserLogsError('MFA user ID not available');
        return;
      }

      const response = await adminApi.get(`/admin/mfa/users/${initialData.id}/logs/`, {
        params: {
          filter: 'totp',
          page: page
        }
      });
      
      if (response.data && response.data.success) {
        setMfaUserLogs(response.data.data || response.data.access_logs || []);
        if (response.data.pagination) {
          setMfaLogsPagination(response.data.pagination);
        }
      } else {
        setMfaUserLogsError(response.data?.message || 'Failed to fetch MFA user logs');
      }
    } catch (err) {
      console.error('Error fetching MFA user logs:', err);
      setMfaUserLogsError(err.message || 'An error occurred while fetching MFA user logs');
    } finally {
      setMfaUserLogsLoading(false);
    }
  };

  // Handle MFA logs pagination
  const handleMfaLogsPageChange = async (newPage) => {
    if (initialData && newPage !== mfaLogsCurrentPage) {
      setMfaLogsCurrentPage(newPage);
      await fetchMfaUserLogs(initialData.email, newPage);
    }
  };

  const handleViewOtpClick = () => {
    fetchOtpCode();
  };

  const handleDisableOtpDisplay = () => {
    setIsOtpDisplayActive(false);
    setOtpCode(null);
    setTimeRemaining(0);
    onClose();
  };

  const handleCopyOtp = () => {
    navigator.clipboard.writeText(otpCode).then(() => {
      setCopySuccess('Copied!');
      setTimeout(() => setCopySuccess(''), 2000); // Clear message after 2 seconds
    }).catch(err => {
      console.error('Failed to copy OTP: ', err);
      setCopySuccess('Failed to copy!');
      setTimeout(() => setCopySuccess(''), 2000);
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setFormErrors({}); // Clear field-specific errors
    setSuccessMessage(null);

    try {
      const dataToSend = {
        ...formData,
        origin_ip_whitelist: formData.origin_ip_whitelist
          .split(',')
          .map((ip) => ip.trim())
          .filter((ip) => ip !== ''),
      };

      // Only include is_enabled if it's an existing user
      if (initialData) {
        dataToSend.is_enabled = formData.is_enabled;
      }

      // Only include secret if creating new user (always required), OR editing existing user AND secret field has a value (user entered something)
      if (!initialData || (initialData && formData.secret.trim() !== '')) {
        dataToSend.secret = formData.secret;
      }

      let response;
      if (initialData) {
        // Update existing MFA user
        response = await adminApi.put(`/admin/mfa/users/${initialData.id}/`, dataToSend);
      } else {
        // Create new MFA user
        response = await adminApi.post('/admin/mfa/users/create/', dataToSend);
      }

      if (response.data && response.data.success) {
        setSuccessMessage(initialData ? 'MFA User updated successfully!' : 'MFA User created successfully!');
        setHasMfaSecretError(false); // Reset MFA secret error flag on successful save
        onSave(); // Callback to refresh list in parent component
        // Reset form data after successful submission
        setFormData({
          first_name: '',
          last_name: '',
          email: '',
          application: '',
          user_type: 'Non-RPA',
          origin_ip_whitelist: '',
          owner_name: '',
          owner_email: '',
          secret: '', // Clear secret after successful creation
        });
      } else {
        setError(response.data?.message || 'Operation failed.');
      }
    } catch (err) {
      console.error('MFA User form submission error:', err);
      if (err.response && err.response.status === 400 && typeof err.response.data === 'object') {
        setFormErrors(err.response.data);
        setError(null); // Clear general error if field errors are present
      } else {
        setError(err.response?.data?.message || err.message || 'An unexpected error occurred.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card">
      <div className="card-body">
        {!showOnlyOtp && (
          <>
            {error && <div className="alert alert-danger">{error}</div>}
            {successMessage && <div className="alert alert-success">{successMessage}</div>}
            <form onSubmit={handleSubmit}>
              {initialData && (
              <div className="mb-3">
                <label htmlFor="mfa_user_uuid" className="form-label">MFA User ID (MiraSol)</label>
                <input
                  type="text"
                  className={`form-control ${formErrors.first_name ? 'is-invalid' : ''}`}
                  id="first_name"
                  name="first_name"
                  value={formData.uuid}
                  onChange={handleChange}
                  required
                />
                {formErrors.first_name && <div className="invalid-feedback">{formErrors.first_name[0]}</div>}
              </div>)}
              <div className="mb-3">
                <label htmlFor="first_name" className="form-label">First Name</label>
                <input
                  type="text"
                  className={`form-control ${formErrors.first_name ? 'is-invalid' : ''}`}
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                />
                {formErrors.first_name && <div className="invalid-feedback">{formErrors.first_name[0]}</div>}
              </div>
              <div className="mb-3">
                <label htmlFor="last_name" className="form-label">Last Name</label>
                <input
                  type="text"
                  className={`form-control ${formErrors.last_name ? 'is-invalid' : ''}`}
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                />
                {formErrors.last_name && <div className="invalid-feedback">{formErrors.last_name[0]}</div>}
              </div>
              <div className="mb-3">
                <label htmlFor="email" className="form-label">Email</label>
                <input
                  type="email"
                  className={`form-control ${formErrors.email ? 'is-invalid' : ''}`}
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
                {formErrors.email && <div className="invalid-feedback">{formErrors.email[0]}</div>}
              </div>

              <div className="mb-3">
                <label htmlFor="secret" className="form-label">MFA Secret</label>
                <div className="input-group">
                  <input
                    type="text"
                    className={`form-control ${formErrors.secret ? 'is-invalid' : ''}`}
                    id="secret"
                    name="secret"
                    value={formData.secret}
                    onChange={handleChange}
                    required={!initialData && isSecretEditable} // Required only for new users when editable
                    disabled={initialData && !isSecretEditable} // Disable if editing and not editable
                    placeholder={initialData && !isSecretEditable ? "*****************" : ""}
                  />
                  {initialData && (
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={() => {
                        setIsSecretEditable(!isSecretEditable);
                        if (!isSecretEditable) { // If enabling edit, clear the secret field
                          setFormData(prevData => ({ ...prevData, secret: '' }));
                        }
                      }}
                    >
                      {isSecretEditable ? 'Cancel' : 'Edit Secret'}
                    </button>
                  )}
                  {formErrors.secret && <div className="invalid-feedback">{formErrors.secret[0]}</div>}
                </div>
                <small className="form-text text-muted">
                  {initialData && !isSecretEditable
                    ? 'Click "Edit Secret" to update the MFA secret. Leave blank to keep current secret.'
                    : 'Enter the secret key from MFA user\'s third-party application\'s QR code.'}
                </small>
                {initialData && isSecretEditable && (
                  <div className="alert alert-warning mt-2">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    <strong>Important:</strong> If you are getting &quot;Invalid MFA secret key&quot; errors when generating TOTP,
                    paste the current secret from your third-party application here and save the user.
                  </div>
                )}
              </div>

              <div className="mb-3">
                <label htmlFor="application" className="form-label">Application</label>
                <input
                  type="text"
                  className={`form-control ${formErrors.application ? 'is-invalid' : ''}`}
                  id="application"
                  name="application"
                  value={formData.application}
                  onChange={handleChange}
                  required
                />
                {formErrors.application && <div className="invalid-feedback">{formErrors.application[0]}</div>}
              </div>
              <div className="mb-3">
                <label htmlFor="user_type" className="form-label">User Type</label>
                <select
                  className={`form-select ${formErrors.user_type ? 'is-invalid' : ''}`}
                  id="user_type"
                  name="user_type"
                  value={formData.user_type}
                  onChange={handleChange}
                  required
                >
                  <option value="RPA">RPA</option>
                  <option value="Non-RPA">Non-RPA</option>
                  <option value="Other">Other</option>
                </select>
                {formErrors.user_type && <div className="invalid-feedback">{formErrors.user_type[0]}</div>}
              </div>
              <div className="mb-3">
                <label htmlFor="origin_ip_whitelist" className="form-label">Origin IP Whitelist (comma-separated)</label>
                <textarea
                  className={`form-control ${formErrors.origin_ip_whitelist ? 'is-invalid' : ''}`}
                  id="origin_ip_whitelist"
                  name="origin_ip_whitelist"
                  rows="3"
                  value={formData.origin_ip_whitelist}
                  onChange={handleChange}
                ></textarea>
                {formErrors.origin_ip_whitelist && <div className="invalid-feedback">{formErrors.origin_ip_whitelist[0]}</div>}
              </div>
              <div className="mb-3">
                <label htmlFor="owner_name" className="form-label">Owner Name</label>
                <input
                  type="text"
                  className={`form-control ${formErrors.owner_name ? 'is-invalid' : ''}`}
                  id="owner_name"
                  name="owner_name"
                  value={formData.owner_name}
                  onChange={handleChange}
                  required
                />
                {formErrors.owner_name && <div className="invalid-feedback">{formErrors.owner_name[0]}</div>}
              </div>
              <div className="mb-3">
                <label htmlFor="owner_email" className="form-label">Owner Email</label>
                <input
                  type="email"
                  className={`form-control ${formErrors.owner_email ? 'is-invalid' : ''}`}
                  id="owner_email"
                  name="owner_email"
                  value={formData.owner_email}
                  onChange={handleChange}
                  required
                />
                {formErrors.owner_email && <div className="invalid-feedback">{formErrors.owner_email[0]}</div>}
              </div>

              {initialData && ( // Only show is_enabled checkbox for existing users
                <div className="mb-3 form-check">
                  <input
                    type="checkbox"
                    className={`form-check-input ${formErrors.is_enabled ? 'is-invalid' : ''}`}
                    id="is_enabled"
                    name="is_enabled"
                    checked={formData.is_enabled}
                    onChange={(e) => setFormData(prevData => ({ ...prevData, is_enabled: e.target.checked }))}
                  />
                  <label className="form-check-label" htmlFor="is_enabled">Enable MFA for this user</label>
                  {formErrors.is_enabled && <div className="invalid-feedback">{formErrors.is_enabled[0]}</div>}
                </div>
              )}
 
               <div className="d-flex justify-content-start">
                 <button type="submit" className="btn btn-primary me-2" disabled={loading}>
                   {loading ? 'Saving...' : (initialData ? 'Update User' : 'Create User')}
                 </button>
                 <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
               </div>
             </form>
           </>
         )}

        {initialData && showOnlyOtp && ( // Only show OTP generation for existing users
          initialData.is_enabled ? (
            <div className="mb-3">
              <h5 className="card-title text-primary mb-3">TOTP Code for {formData.email}</h5>
              {error && <div className="alert alert-danger">{error}</div>}
              {isOtpDisplayActive ? (
                <>
                  {otpCode ? (
                      <div className="alert alert-info text-center">
                        <h1 className="fw-bold fs-3">{otpCode}
                        <button className="btn" onClick={handleCopyOtp} title="Copy OTP">
                          <i className="fas fa-copy"></i>
                        </button></h1>
                        {copySuccess && <small className="text-success">{copySuccess}</small>}
                      <p className="fs-6">Expires in {Math.floor(timeRemaining)} seconds</p>
                      </div>
                  ) : (
                    <div className="alert alert-warning text-center">
                      <p>Generating OTP...</p>
                    </div>
                  )}
                </>
              ) : (
                <button
                  type="button"
                  className={`btn w-100 ${hasMfaSecretError ? 'border-0' : 'btn-info'}`}
                  onClick={handleViewOtpClick}
                  disabled={loading || hasMfaSecretError}
                  title={hasMfaSecretError ? 'Fix MFA secret key error first' : ''}
                >
                  {loading ? 'Loading...' : (hasMfaSecretError ? 'TOTP disabled due to an error' : 'Generate TOTP')}
                </button>
              )}
            </div>
          ) : (
            <p className="card-title text-center">Can&apos;t generate TOTP for this disabled MFA user</p>
          )
        )}
        {initialData && showOnlyOtp && (
          <div className="mt-4">
            <h5 className="mb-3">
              MFA User Access Logs
              <button className="btn btn-link p-0 ms-2" onClick={() => setShowMfaLogs(!showMfaLogs)}>
                <i className={`fas ${showMfaLogs ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
              </button>
            </h5>
            {showMfaLogs && (
              mfaUserLogsLoading ? (
                <div className="text-center">Loading logs...</div>
              ) : mfaUserLogsError ? (
                <div className="alert alert-danger">Error loading logs: {mfaUserLogsError}</div>
              ) : mfaUserLogs.length === 0 ? (
                <div className="alert alert-info">No MFA access logs found for this user.</div>
              ) : (
                <AgGridTable
                  rowData={mfaUserLogs}
                  height={400}
                  columnDefs={[
                    {
                      headerName: 'Timestamp',
                      field: 'timestamp',
                      flex: 1,
                      valueFormatter: (params) => {
                        if (!params.value) return '';
                        return new Date(params.value).toLocaleString();
                      }
                    },
                    {
                      headerName: 'Access Type',
                      field: 'access_type',
                      flex: 1
                    },
                    {
                      headerName: 'Result',
                      field: 'result',
                      width: 120,
                      cellRenderer: (params) => {
                        if (!params.value) return null;
                        const badgeClass = params.value === 'success' ? 'bg-success' : 'bg-danger';
                        return (
                          <span className={`badge ${badgeClass}`}>
                            {params.value}
                          </span>
                        );
                      }
                    },
                    {
                      headerName: 'IP Address',
                      field: 'client_ip_address',
                      flex: 1
                    }
                  ]}
                  defaultColDef={{
                    resizable: true,
                    sortable: true,
                    filter: true
                  }}
                  gridOptions={{
                    domLayout: 'autoHeight',
                    autoSizeStrategy: { type: 'fitGridWidth' }
                  }}
                  getRowId={(params) => params.data.id}
                />
              )
            )}
            
            {/* Pagination Controls for MFA User Logs */}
            {/* {showMfaLogs && mfaLogsPagination && (
              <div className="mt-3">
                <PaginationControls
                  pagination={mfaLogsPagination}
                  onPageChange={handleMfaLogsPageChange}
                />
              </div>
            )} */}
          </div>
        )}

      </div>
    </div>
  );
};

export default MfaUserForm;